import {
  useMutation,
  DocumentNode,
  MutationHookOptions,
  TypedDocumentNode,
  OperationVariables,
} from "@apollo/client";
import { gql } from "generated-graphql";
import { GET_SUBMISSION_QUERY } from "queries/submissions";

import { useAlerts } from "../components/Alerts/AlertProvider";
import { GET_TIERII_REPORT } from "../routes/Staff/Fulfillment/FulfillmentDetails/useReportDetails";
import { useCurrentUser } from "./useCurrentUser";

export const UPSERT_COMMENT = gql(`
  mutation upsertComment($input: CommentInput!) {
    upsertComment(input: $input) {
      id
      userId
      reportId
      workflowId
      activityId
      submissionId
      comment
      isSystemMessage
      createdAt
      updatedAt
      user {
        id
        email
        person {
          first
          last
        }
      }
    }
  }
`);

export const DELETE_COMMENT = gql(`
  mutation deleteComment($id: ID!) {
    deleteComment(id: $id)
  }
`);

interface CacheUpdateConfig {
  query: DocumentNode | TypedDocumentNode<any, OperationVariables>;
  variables: (comment: any) => Record<string, any>;
  updateData: (existingData: any, newComment: any) => any;
}

interface UseCreateCommentOptions {
  cacheUpdate?: CacheUpdateConfig;
  successMessage?: string;
  errorMessage?: string;
  enableOptimisticResponse?: boolean;
  onCompleted?: (data: any) => void;
  onError?: (error: any) => void;
}

interface DeleteCacheUpdateConfig {
  query: DocumentNode | TypedDocumentNode<any, OperationVariables>;
  variables: (comment: any) => Record<string, any>;
  updateData?: (existingData: any, deletedCommentId: string) => any; // Optional custom update logic
}

interface UseDeleteCommentOptions {
  cacheUpdate?: DeleteCacheUpdateConfig;
  successMessage?: string;
  errorMessage?: string;
  onCompleted?: (data: any) => void;
  onError?: (error: any) => void;
}

/**
 * Generic hook for creating comments with configurable cache updates
 *
 * This is the base hook that allows you to configure:
 * - Custom cache update logic
 * - Custom success/error messages
 * - Optimistic responses
 * - Completion/error callbacks
 *
 * @example
 * ```tsx
 * const [createComment] = useCreateComment({
 *   cacheUpdate: {
 *     query: GET_MY_QUERY,
 *     variables: (comment) => ({ id: comment.entityId }),
 *     updateData: (existing, newComment) => ({
 *       ...existing,
 *       myEntity: {
 *         ...existing.myEntity,
 *         comments: [newComment, ...existing.myEntity.comments],
 *       },
 *     }),
 *   },
 *   successMessage: "Comment added to entity!",
 * });
 * ```
 */
export const useCreateComment = (options: UseCreateCommentOptions = {}) => {
  const alerts = useAlerts();
  const { user } = useCurrentUser();

  const {
    cacheUpdate,
    successMessage = "Comment added successfully",
    errorMessage = "An error occurred while updating the comment",
    enableOptimisticResponse = true,
    onCompleted,
    onError,
  } = options;

  const mutationOptions: MutationHookOptions = {
    onCompleted(data) {
      alerts.success(successMessage);
      onCompleted?.(data);
    },
    onError(error) {
      console.error(error);
      alerts.error(errorMessage);
      onError?.(error);
    },
  };

  // Add cache update logic if provided
  if (cacheUpdate) {
    mutationOptions.update = (cache, { data }) => {
      if (!data?.upsertComment) {
        return;
      }

      const newComment = data.upsertComment;
      const variables = cacheUpdate.variables(newComment);

      const existingData = cache.readQuery({
        query: cacheUpdate.query,
        variables,
      });

      if (!existingData) {
        return;
      }

      const updatedData = cacheUpdate.updateData(existingData, newComment);

      cache.writeQuery({
        query: cacheUpdate.query,
        variables,
        data: updatedData,
      });
    };
  }

  // Add optimistic response if enabled
  if (enableOptimisticResponse) {
    mutationOptions.optimisticResponse = ({ input }: { input: any }) => {
      return {
        upsertComment: {
          __typename: "Comment" as const,
          id: input.id ?? "new",
          comment: input.comment,
          reportId: input.reportId ?? null,
          workflowId: input.workflowId ?? null,
          activityId: input.activityId ?? null,
          submissionId: input.submissionId ?? null,
          isSystemMessage: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          userId: user?.id,
          user: user
            ? {
                ...user,
                person: user.person || { first: "", last: "" },
              }
            : null,
        },
      };
    };
  }

  const mutation = useMutation(UPSERT_COMMENT as any, mutationOptions);
  return mutation;
};

/**
 * Generic hook for deleting comments with configurable cache updates
 *
 * This is the base hook that allows you to configure:
 * - Custom cache update logic for different entity types
 * - Custom success/error messages
 * - Completion/error callbacks
 *
 * @example
 * ```tsx
 * const [deleteComment] = useDeleteComment({
 *   cacheUpdate: {
 *     query: GET_MY_ENTITY,
 *     variables: (comment) => ({ entityId: comment.entityId }),
 *     updateData: (existing, deletedCommentId) => ({
 *       ...existing,
 *       myEntity: {
 *         ...existing.myEntity,
 *         comments: existing.myEntity.comments.filter(c => c.id !== deletedCommentId),
 *       },
 *     }),
 *   },
 *   successMessage: "Comment deleted successfully!",
 * });
 * ```
 */
export const useDeleteComment = (options: UseDeleteCommentOptions = {}) => {
  const alerts = useAlerts();

  const {
    cacheUpdate,
    successMessage = "Comment deleted successfully",
    errorMessage = "An error occurred while deleting the comment",
    onCompleted,
    onError,
  } = options;

  const mutationOptions: MutationHookOptions = {
    onCompleted(data) {
      alerts.success(successMessage);
      onCompleted?.(data);
    },
    onError(error) {
      console.error(error);
      alerts.error(errorMessage);
      onError?.(error);
    },
  };

  // Add cache update logic if provided
  if (cacheUpdate) {
    mutationOptions.update = (cache, { data }, { variables: mutationVariables }) => {
      if (!data?.deleteComment) {
        return;
      }

      try {
        // Use the comment ID from the mutation variables, not the return data
        const deletedCommentId = mutationVariables?.id;
        const variables = cacheUpdate.variables({
          submissionId: deletedCommentId,
        });

        const existingData = cache.readQuery({
          query: cacheUpdate.query,
          variables,
        });

        if (!existingData) {
          return;
        }

        // Use custom update logic if provided, otherwise default to marking as deleted
        const updatedData = cacheUpdate.updateData
          ? cacheUpdate.updateData(existingData, deletedCommentId)
          : existingData; // Default: rely on the mutation response to update the cache

        cache.writeQuery({
          query: cacheUpdate.query,
          variables,
          data: updatedData,
        });
      } catch (error) {
        console.warn("Failed to update comment delete cache:", error);
        // Don't throw - let the mutation succeed even if cache update fails
      }
    };
  }

  const mutation = useMutation(DELETE_COMMENT as any, mutationOptions);
  return mutation;
};

/**
 * Specific hook for TierII report comments - maintains backward compatibility
 *
 * This hook automatically handles cache updates for the GET_TIERII_REPORT query.
 * Use this when adding comments to TierII reports where you need the cache
 * to be updated immediately without refetching
 */
export const useCreateTierIIReportComment = () => {
  return useCreateComment({
    cacheUpdate: {
      query: GET_TIERII_REPORT,
      variables: (comment) => ({ reportId: comment.reportId ?? "" }),
      updateData: (existingData, newComment) => ({
        __typename: "Query",
        tierIIReport: {
          ...existingData.tierIIReport,
          comments: [newComment, ...(existingData.tierIIReport.comments ?? [])],
        },
      }),
    },
  });
};

/**
 * Specific hook for deleting TierII report comments
 *
 * @example
 * ```tsx
 * const [deleteComment, { loading }] = useDeleteTierIIReportComment();
 *
 * await deleteComment({
 *   variables: { id: "comment-id-to-delete" },
 * });
 * ```
 */
export const useDeleteTierIIReportComment = () => {
  return useDeleteComment({
    cacheUpdate: {
      query: GET_TIERII_REPORT,
      variables: (comment) => ({ reportId: comment.reportId ?? "" }),
      updateData: (existingData, deletedCommentId) => ({
        __typename: "Query",
        tierIIReport: {
          ...existingData.tierIIReport,
          comments:
            existingData.tierIIReport.comments?.filter((c: any) => c.id !== deletedCommentId) ?? [],
        },
      }),
    },
  });
};

export const useCreateSubmissionComment = () => {
  const [addComment, { loading }] = useCreateComment();

  const handleAddComment = async (submissionId: string, message: string) => {
    return await addComment({
      variables: {
        input: {
          submissionId,
          comment: message,
        },
      },
    });
  };

  return { addComment: handleAddComment, loading };
};

export const useDeleteSubmissionComment = () => {
  const [deleteComment, { loading }] = useDeleteComment();

  const handleDeleteComment = async (commentId: string) => {
    await deleteComment({
      variables: { id: commentId },
      // Update cache manually here where we have access to both commentId and submissionId
      update: (cache, { data }) => {
        if (!data?.deleteComment) return;

        // Find all cached submissions and remove the comment from each
        const cacheData = cache.extract();
        for (const key in cacheData) {
          if (key.startsWith("Submission:")) {
            try {
              // Use cache.modify to safely update the comments array
              cache.modify({
                id: key,
                fields: {
                  comments(existingComments = [], { readField }) {
                    return existingComments.filter(
                      (commentRef: any) => readField("id", commentRef) !== commentId
                    );
                  },
                },
              });
            } catch (e) {
              // Ignore cache modification errors for submissions not currently loaded
            }
          }
        }
      },
    });
  };

  return { deleteComment: handleDeleteComment, loading };
};

export const useUpdateComment = () => {
  const alerts = useAlerts();
  const mutation = useMutation(UPSERT_COMMENT, {
    // apollo should(tm) update the cache automatically
    onCompleted() {
      alerts.success(`Comment added successfully`);
    },
    onError(error) {
      console.error(error);
      alerts.error(`An error occurred while updating the comment`);
    },
  });

  return mutation;
};
